import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  AlertTriangle,
  Bell,
  ChevronDown,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import {
  fetchCartographie,
  fetchEtatParMaladie,
  fetchSantePublique,
  fetchSurveillanceGlobale,
  type CartographieData,
  type EtatParMaladieItem,
  type Period,
  type SantePubliqueData,
  type SurveillanceGlobaleData,
} from "@/api/ministereStatistiques";
import { DonutChart } from "./stats/DonutChart";
import { GaugeChart } from "./stats/GaugeChart";
import { HorizontalBar } from "./stats/HorizontalBar";
import { LineChart } from "./stats/LineChart";
import { VerticalBar } from "./stats/VerticalBar";

// ─── Constantes ───────────────────────────────────────────────────────────────

type Tab = "platform" | "population" | "epidemic";

const NIVEAU_COLORS: Record<string, string> = {
  info: "#3b82f6",
  attention: "#f59e0b",
  critique: "#f97316",
  urgence: "#ef4444",
};

const NIVEAU_LABELS: Record<string, string> = {
  info: "Info",
  attention: "Attention",
  critique: "Critique",
  urgence: "Urgence",
};

const STATUT_COLORS: Record<string, string> = {
  aucune: "#94a3b8",
  en_veille: "#3b82f6",
  alerte: "#f97316",
  "épidémie": "#ef4444",
};

const STATUT_LABELS: Record<string, string> = {
  aucune: "Aucune",
  en_veille: "Veille",
  alerte: "Alerte",
  "épidémie": "Épidémie",
};

const EVOLUTION_ICONS: Record<string, string> = {
  hausse: "↑",
  baisse: "↓",
  stable: "→",
};

const PERIOD_OPTIONS: Array<{ k: Period; label: string }> = [
  { k: "30j", label: "30 jours" },
  { k: "60j", label: "60 jours" },
  { k: "90j", label: "90 jours" },
  { k: "6m", label: "06 mois" },
  { k: "9m", label: "09 mois" },
  { k: "12m", label: "12 mois" },
  { k: "1a", label: "1 an" },
];

// ─── Sous-composants ──────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  color = "default",
}: {
  label: string;
  value: string | number;
  color?: "default" | "green" | "orange" | "red" | "blue";
}) {
  const valueClass =
    color === "green"
      ? "text-green-700"
      : color === "orange"
        ? "text-orange-500"
        : color === "red"
          ? "text-red-600"
          : color === "blue"
            ? "text-blue-600"
            : "text-[#3F3F46]";

  return (
    <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
      <div className={`text-4xl font-semibold ${valueClass}`}>{value}</div>
      <div className="mt-1 text-sm text-gray-500">{label}</div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#00553E] border-t-transparent" />
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="mb-4 text-base font-semibold text-[#0B1F16]">{children}</h2>;
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function MinistryDashboard() {
  const [user, setUser] = useState<{ ident?: string } | null>(null);
  const [tab, setTab] = useState<Tab>("platform");
  const [period, setPeriod] = useState<Period>("30j");
  const [periodOpen, setPeriodOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifTab, setNotifTab] = useState<"all" | "unread">("all");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [panelPos, setPanelPos] = useState<{ left: number; top: number }>({ left: 280, top: 70 });
  const [notifs, setNotifs] = useState<Array<{ id: string; text: string; time: string; read: boolean }>>([
    { id: "m1", text: "Le dossier de DANGBEGNON Ruben vient d'être ajouté par DOE John", time: "il y a 2h", read: false },
    { id: "m2", text: "Un communiqué a été publié", time: "il y a 1j", read: true },
    { id: "m3", text: "Mise à jour des statistiques population", time: "il y a 3j", read: true },
  ]);
  const unread = notifs.filter((n) => !n.read).length;
  const asideRef = useRef<HTMLDivElement>(null);

  // ── Données API
  const [surveillance, setSurveillance] = useState<SurveillanceGlobaleData | null>(null);
  const [surveillanceLoading, setSurveillanceLoading] = useState(false);
  const [maladies, setMaladies] = useState<EtatParMaladieItem[]>([]);
  const [carto, setCarto] = useState<CartographieData | null>(null);
  const [staticLoading, setStaticLoading] = useState(true);
  const [santePublique, setSantePublique] = useState<SantePubliqueData | null>(null);
  const [santeLoading, setSanteLoading] = useState(false);

  // Chargement localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem("ministry.user");
      if (raw) setUser(JSON.parse(raw));
    } catch {}
    try {
      const nraw = localStorage.getItem("ministry.notifications");
      if (nraw) {
        const arr = JSON.parse(nraw);
        if (Array.isArray(arr))
          setNotifs((prev) => {
            const seedIds = new Set(prev.map((p) => p.id));
            const restored = arr.filter(
              (x: { id?: string }) => x && x.id && !seedIds.has(x.id)
            );
            return [...restored, ...prev];
          });
      }
    } catch {}
    const onStorage = (event: StorageEvent) => {
      if (event.key === "ministry.notifications" && event.newValue) {
        try {
          const arr = JSON.parse(event.newValue);
          if (Array.isArray(arr)) setNotifs(arr);
        } catch {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Chargement données statiques (une seule fois)
  useEffect(() => {
    setStaticLoading(true);
    Promise.allSettled([fetchEtatParMaladie(), fetchCartographie()]).then(
      ([maladieRes, cartoRes]) => {
        if (maladieRes.status === "fulfilled" && maladieRes.value.ok)
          setMaladies(maladieRes.value.data);
        if (cartoRes.status === "fulfilled" && cartoRes.value.ok)
          setCarto(cartoRes.value.data);
        setStaticLoading(false);
      }
    );
  }, []);

  // Chargement données période
  const loadPeriodData = useCallback((p: Period) => {
    setSurveillanceLoading(true);
    setSanteLoading(true);
    fetchSurveillanceGlobale(p).then((res) => {
      if (res.ok) setSurveillance(res.data);
      setSurveillanceLoading(false);
    });
    fetchSantePublique(p).then((res) => {
      if (res.ok) setSantePublique(res.data);
      setSanteLoading(false);
    });
  }, []);

  useEffect(() => {
    loadPeriodData(period);
  }, [period, loadPeriodData]);

  const saveNotifs = (
    list: Array<{ id: string; text: string; time: string; read: boolean }>
  ) => {
    try {
      localStorage.setItem("ministry.notifications", JSON.stringify(list));
    } catch {}
  };

  // ── Données onglet plateforme
  const plateforme = santePublique?.plateforme;
  const population = santePublique?.population;

  // ── Maladies nécessitant action immédiate
  const alertesImmédiates = maladies.filter(
    (m) => m.action_immediate && m.statutAlerte !== "aucune"
  );

  // ── Alertes par département pour le bar chart
  const alertesDeptData = carto
    ? Object.entries(carto.alertesParDepartement)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]) => ({ name: k, value: v }))
    : [];

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-12">
      {/* ── Sidebar desktop ── */}
      <aside
        ref={asideRef}
        className="hidden md:flex md:col-span-3 lg:col-span-2 border-r bg-white p-4 md:p-6 flex-col relative"
        style={{ borderColor: "#E5E7EB" }}
      >
        <div className="flex items-center justify-between">
          <div className="relative">
            <button onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-2">
              <div className="font-semibold text-[#0B1F16]">{user?.ident || "Ministère"}</div>
              <ChevronDown size={16} className="text-gray-600" />
            </button>
            {menuOpen && (
              <div
                className="absolute left-0 mt-2 w-40 bg-white rounded-[10px] border shadow"
                style={{ borderColor: "#E5E7EB" }}
              >
                <button
                  onClick={() => setMenuOpen(false)}
                  className="block w-full text-left px-3 py-2 hover:bg-gray-50"
                >
                  Profil
                </button>
                <button
                  onClick={() => {
                    localStorage.removeItem("ministry.user");
                    setMenuOpen(false);
                    location.href = "/ministere/connexion";
                  }}
                  className="block w-full text-left px-3 py-2 hover:bg-gray-50 text-red-600"
                >
                  Déconnexion
                </button>
              </div>
            )}
          </div>
          <button
            aria-label="Notifications"
            className="text-[#0B1F16] relative"
            onClick={() => {
              const rect = asideRef.current?.getBoundingClientRect();
              if (rect)
                setPanelPos({ left: Math.round(rect.right + 24), top: Math.round(rect.top + 24) });
              setNotifOpen((v) => !v);
            }}
          >
            <Bell size={18} />
            {unread > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500" />}
          </button>
        </div>

        <div className="flex-1 flex flex-col justify-center">
          <nav className="space-y-1">
            <NavLink
              to="/ministere/dashboard"
              className={({ isActive }) =>
                `block px-3 py-2 rounded-[8px] text-sm ${
                  isActive
                    ? "text-[#00553E] font-semibold border-l-4 border-[#00553E] bg-transparent"
                    : "text-[#0B1F16] hover:bg-[#F3F4F4]"
                }`
              }
            >
              Tableau de bord
            </NavLink>
            <NavLink
              to="/ministere/communique"
              className={({ isActive }) =>
                `block px-3 py-2 rounded-[8px] text-sm ${
                  isActive
                    ? "text-[#00553E] font-semibold border-l-4 border-[#00553E] bg-transparent"
                    : "text-[#0B1F16] hover:bg-[#F3F4F4]"
                }`
              }
            >
              Lancer un communiqué
            </NavLink>
          </nav>
        </div>

        <button
          onClick={() => {
            localStorage.removeItem("ministry.user");
            location.href = "/ministere/connexion";
          }}
          className="mt-auto flex items-center gap-2 text-red-500 font-semibold text-sm"
        >
          <span>Déconnexion</span>
          <LogOut size={16} />
        </button>

        {/* Panel notifications desktop */}
        {notifOpen && (
          <div
            className="fixed z-50 w-[420px] max-w-[90vw] bg-white rounded-[10px] border shadow"
            style={{ borderColor: "#E5E7EB", left: `${panelPos.left}px`, top: `${panelPos.top}px` }}
          >
            <div className="px-5 pt-4 pb-3">
              <div className="text-center font-semibold">Centre de notifications</div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <div className="flex gap-6">
                  {(["all", "unread"] as const).map((t) => (
                    <button
                      key={t}
                      className={`${notifTab === t ? "font-semibold text-[#0B1F16] border-b-2" : "text-gray-500"}`}
                      style={{ borderColor: "#0B1F16" }}
                      onClick={() => setNotifTab(t)}
                    >
                      {t === "all" ? "Toutes" : "Non lues"}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() =>
                    setNotifs((prev) => {
                      const next = prev.map((n) => ({ ...n, read: true }));
                      saveNotifs(next);
                      return next;
                    })
                  }
                  className={`font-semibold ${unread ? "text-[#0B9D78]" : "text-gray-300"}`}
                  disabled={!unread}
                >
                  Tout marquer comme lues
                </button>
              </div>
            </div>
            <div className="border-t" style={{ borderColor: "#E5E7EB" }} />
            <div className="max-h-[360px] overflow-auto">
              {(() => {
                const list = notifs.filter((n) => (notifTab === "all" ? true : !n.read));
                if (list.length === 0)
                  return <div className="px-5 py-10 text-center text-gray-600">Aucune notification.</div>;
                return list.map((n) => (
                  <div key={n.id} className={`px-5 py-3 text-sm ${!n.read ? "bg-[#DCEFE9]" : ""}`}>
                    <div className="text-gray-800">{n.text}</div>
                    <div className="mt-1 text-xs text-gray-500">{n.time}</div>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}
      </aside>

      {/* ── Menu mobile ── */}
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileNavOpen(false)} />
          <div
            className="absolute left-0 top-0 h-full w-72 bg-white border-r p-4 flex flex-col"
            style={{ borderColor: "#E5E7EB" }}
          >
            <div className="flex items-center justify-between">
              <div className="font-semibold text-[#0B1F16]">{user?.ident || "Ministère"}</div>
              <button aria-label="Fermer" className="text-[#0B1F16]" onClick={() => setMobileNavOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="mt-4">
              <nav className="space-y-1">
                {[
                  { to: "/ministere/dashboard", label: "Tableau de bord" },
                  { to: "/ministere/communique", label: "Lancer un communiqué" },
                ].map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `block px-3 py-2 rounded-[8px] text-sm ${
                        isActive
                          ? "text-[#00553E] font-semibold border-l-4 border-[#00553E]"
                          : "text-[#0B1F16] hover:bg-[#F3F4F4]"
                      }`
                    }
                    onClick={() => setMobileNavOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
            <button
              onClick={() => {
                localStorage.removeItem("ministry.user");
                location.href = "/ministere/connexion";
              }}
              className="mt-auto flex items-center gap-2 text-red-500 font-semibold text-sm"
            >
              <span>Déconnexion</span>
              <LogOut size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ── Contenu principal ── */}
      <main className="md:col-span-9 lg:col-span-10 bg-[#F3F3F2]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
          {/* Header mobile */}
          <div className="md:hidden flex items-center justify-between mb-3">
            <button
              aria-label="Menu"
              className="h-9 w-9 rounded-[10px] border flex items-center justify-center text-[#0B1F16]"
              style={{ borderColor: "#D1E2DD" }}
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu size={18} />
            </button>
            <button
              aria-label="Notifications"
              className="text-[#0B1F16] relative"
              onClick={() => setNotifOpen((v) => !v)}
            >
              <Bell size={18} />
              {unread > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500" />}
            </button>
          </div>

          {/* Panel notifications mobile */}
          {notifOpen && (
            <div
              className="md:hidden fixed right-4 top-16 z-50 w-[320px] max-w-[90vw] bg-white rounded-[10px] border shadow"
              style={{ borderColor: "#E5E7EB" }}
            >
              <div className="px-5 pt-4 pb-3">
                <div className="text-center font-semibold">Centre de notifications</div>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <div className="flex gap-6">
                    {(["all", "unread"] as const).map((t) => (
                      <button
                        key={t}
                        className={`${notifTab === t ? "font-semibold text-[#0B1F16] border-b-2" : "text-gray-500"}`}
                        style={{ borderColor: "#0B1F16" }}
                        onClick={() => setNotifTab(t)}
                      >
                        {t === "all" ? "Toutes" : "Non lues"}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setNotifs((prev) => prev.map((n) => ({ ...n, read: true })))}
                    className={`font-semibold ${unread ? "text-[#0B9D78]" : "text-gray-300"}`}
                    disabled={!unread}
                  >
                    Tout marquer comme lues
                  </button>
                </div>
              </div>
              <div className="border-t" style={{ borderColor: "#E5E7EB" }} />
              <div className="max-h-[360px] overflow-auto">
                {(() => {
                  const list = notifs.filter((n) => (notifTab === "all" ? true : !n.read));
                  if (list.length === 0)
                    return <div className="px-5 py-10 text-center text-gray-600">Aucune notification.</div>;
                  return list.map((n) => (
                    <div key={n.id} className={`px-5 py-3 text-sm ${!n.read ? "bg-[#DCEFE9]" : ""}`}>
                      <div className="text-gray-800">{n.text}</div>
                      <div className="mt-1 text-xs text-gray-500">{n.time}</div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          <div className="text-4xl font-semibold" style={{ color: "#0B1F16" }}>
            Tableau de bord
          </div>

          {/* Onglets + sélecteur période */}
          <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="-mx-4 md:mx-0 overflow-x-auto">
              <div className="px-4 md:px-0 flex gap-2 min-w-max pb-1">
                {([
                  { k: "platform" as Tab, label: "Statistique de la plateforme" },
                  { k: "population" as Tab, label: "Statistique de la population" },
                  { k: "epidemic" as Tab, label: "Statistique épidémie" },
                ] as const).map((item) => (
                  <button
                    key={item.k}
                    onClick={() => setTab(item.k)}
                    className="h-8 md:h-9 px-2.5 md:px-3 rounded-[10px] text-xs md:text-sm whitespace-nowrap"
                    style={{
                      backgroundColor: tab === item.k ? "#00553E" : "#E5E7EB",
                      color: tab === item.k ? "#FFFFFF" : "#0B1F16",
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sélecteur période */}
            <div className="relative mt-2 md:mt-0 self-start md:self-auto">
              <button
                className="h-9 px-4 rounded-[10px] border bg-white flex items-center gap-2 text-sm"
                style={{ borderColor: "#D1E2DD" }}
                onClick={() => setPeriodOpen((v) => !v)}
              >
                {PERIOD_OPTIONS.find((p) => p.k === period)?.label ?? period}
                <span>▾</span>
              </button>
              {periodOpen && (
                <div
                  className="absolute left-0 top-full mt-2 w-36 bg-white border rounded-[10px] shadow z-50"
                  style={{ borderColor: "#E5E7EB" }}
                >
                  {PERIOD_OPTIONS.map((opt) => (
                    <div
                      key={opt.k}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      onClick={() => {
                        setPeriod(opt.k);
                        setPeriodOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Onglet Plateforme ── */}
          {tab === "platform" && (
            <div className="mt-6 space-y-6">
              {santeLoading ? (
                <Spinner />
              ) : plateforme ? (
                <>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                    <StatCard label="Inscrits" value={plateforme.totalInscrits.toLocaleString()} />
                    <StatCard label="Nouveaux inscrits" value={plateforme.nouveauxInscrits.toLocaleString()} color="blue" />
                    <StatCard label="Professionnels actifs" value={plateforme.professionnelsActifs.toLocaleString()} color="green" />
                    <StatCard label="Établissements actifs" value={plateforme.etablissementsActifs.toLocaleString()} color="green" />
                    <StatCard label="Consultations réalisées" value={plateforme.consultationsRealisees.toLocaleString()} />
                    <StatCard label="Ordonnances délivrées" value={plateforme.ordonnancesDelivrees.toLocaleString()} />
                    <StatCard label="Analyses prescrites" value={plateforme.analysesPrescrites.toLocaleString()} />
                    {plateforme.tauxDeces !== undefined && (
                      <StatCard label="Taux de décès" value={`${plateforme.tauxDeces}%`} color="red" />
                    )}
                  </div>
                  {surveillance?.evolutionMensuelle && surveillance.evolutionMensuelle.length > 0 && (
                    <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
                      <SectionTitle>Évolution des alertes épidémiques</SectionTitle>
                      <LineChart
                        data={surveillance.evolutionMensuelle.map((m) => ({
                          label: m.periode,
                          value: m.totalAlertes,
                        }))}
                        color="#FF6B57"
                        height={220}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-10 text-center text-gray-500">Données indisponibles</div>
              )}
            </div>
          )}

          {/* ── Onglet Population ── */}
          {tab === "population" && (
            <div className="mt-6 space-y-6">
              {santeLoading ? (
                <Spinner />
              ) : population ? (
                <>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    {/* Genre */}
                    <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
                      <SectionTitle>Répartition par genre</SectionTitle>
                      <DonutChart
                        data={population.repartitionGenres.map((g) => ({
                          name: g.genre === "homme" ? "Homme" : g.genre === "femme" ? "Femme" : g.genre,
                          value: g.nombre,
                        }))}
                        colors={["#3b82f6", "#ec4899", "#94a3b8"]}
                        height={260}
                      />
                    </div>

                    {/* Couverture assurance */}
                    <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
                      <SectionTitle>Couverture assurance maladie</SectionTitle>
                      <DonutChart
                        data={[
                          { name: "AMO", value: population.couvertureAssurance.AMO },
                          { name: "Privé", value: population.couvertureAssurance.prive },
                          { name: "Mutuelle", value: population.couvertureAssurance.mutuelle },
                          { name: "Autre", value: population.couvertureAssurance.autre },
                          { name: "Non couvert", value: population.couvertureAssurance.nonCouvert },
                        ]}
                        colors={["#22c55e", "#3b82f6", "#a855f7", "#f59e0b", "#ef4444"]}
                        height={260}
                      />
                    </div>
                  </div>

                  {/* Tranches d'âge */}
                  {population.repartitionAges.length > 0 && (
                    <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
                      <SectionTitle>Répartition par tranche d'âge</SectionTitle>
                      <HorizontalBar
                        data={population.repartitionAges.map((a) => ({
                          name: a.trancheAge,
                          value: a.nombre,
                        }))}
                        color="#00553E"
                        height={200}
                      />
                    </div>
                  )}

                  {/* Régions */}
                  {population.repartitionRegions.length > 0 && (
                    <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
                      <SectionTitle>Distribution par région</SectionTitle>
                      <VerticalBar
                        data={population.repartitionRegions.map((r) => ({
                          name: r.region,
                          value: r.nombre,
                        }))}
                        color="#3b82f6"
                        height={260}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-10 text-center text-gray-500">Données indisponibles</div>
              )}
            </div>
          )}

          {/* ── Onglet Épidémie ── */}
          {tab === "epidemic" && (
            <div className="mt-6 space-y-6">
              {/* Bannière maladies à action immédiate */}
              {alertesImmédiates.length > 0 && (
                <div
                  className="flex items-start gap-3 rounded-[10px] border border-red-200 bg-red-50 px-4 py-3"
                >
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                  <div>
                    <p className="font-semibold text-red-700">Action immédiate requise</p>
                    <p className="mt-0.5 text-sm text-red-600">
                      {alertesImmédiates.map((m) => m.nom).join(" · ")}
                    </p>
                  </div>
                </div>
              )}

              {surveillanceLoading ? (
                <Spinner />
              ) : surveillance ? (
                <>
                  {/* KPI alertes + gauge */}
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <StatCard label="Alertes actives" value={surveillance.alertes.actives} color="red" />
                    <StatCard label="Alertes résolues" value={surveillance.alertes.resolues} color="green" />
                    <StatCard label="Alertes ignorées" value={surveillance.alertes.ignorees} />
                    <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
                      <p className="text-xs text-gray-500 mb-1">Taux de résolution</p>
                      <GaugeChart
                        value={surveillance.tauxResolution}
                        label="Résolution"
                        targetThreshold={90}
                        height={160}
                      />
                    </div>
                  </div>

                  {/* Niveaux + maladies fréquentes */}
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
                      <SectionTitle>Niveaux d'alerte</SectionTitle>
                      <DonutChart
                        data={Object.entries(surveillance.parNiveau).map(([k, v]) => ({
                          name: NIVEAU_LABELS[k] ?? k,
                          value: v,
                        }))}
                        colors={Object.keys(surveillance.parNiveau).map(
                          (k) => NIVEAU_COLORS[k] ?? "#94a3b8"
                        )}
                        height={260}
                      />
                    </div>
                    {surveillance.maladiesFrequentes.length > 0 && (
                      <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
                        <SectionTitle>Maladies les plus fréquentes</SectionTitle>
                        <HorizontalBar
                          data={surveillance.maladiesFrequentes.slice(0, 5).map((m) => ({
                            name: m.maladie,
                            value: m.nombreCas,
                          }))}
                          color="#FF6B57"
                        />
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-4 text-center text-gray-500">Données surveillance indisponibles</div>
              )}

              {/* Tableau MPE */}
              {!staticLoading && maladies.length > 0 && (
                <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
                  <SectionTitle>État par maladie (référentiel MPE)</SectionTitle>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-gray-500" style={{ borderColor: "#E5E7EB" }}>
                          <th className="pb-2 pr-4 font-medium">Maladie</th>
                          <th className="pb-2 pr-4 font-medium">Statut</th>
                          <th className="pb-2 pr-4 font-medium text-right">Cas</th>
                          <th className="pb-2 pr-4 font-medium">Évolution</th>
                          <th className="pb-2 font-medium">Régions actives</th>
                        </tr>
                      </thead>
                      <tbody>
                        {maladies.map((m) => (
                          <tr key={m.nom} className="border-b" style={{ borderColor: "#F3F4F6" }}>
                            <td className="py-2 pr-4 font-medium text-[#0B1F16]">
                              {m.nom}
                              {m.action_immediate && m.statutAlerte !== "aucune" && (
                                <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-red-500" />
                              )}
                            </td>
                            <td className="py-2 pr-4">
                              <span
                                className="inline-block rounded-full px-2 py-0.5 text-xs font-medium text-white"
                                style={{ backgroundColor: STATUT_COLORS[m.statutAlerte] ?? "#94a3b8" }}
                              >
                                {STATUT_LABELS[m.statutAlerte] ?? m.statutAlerte}
                              </span>
                            </td>
                            <td className="py-2 pr-4 text-right font-mono">{m.nombreCas}</td>
                            <td className="py-2 pr-4 text-gray-500">
                              {m.evolution
                                ? `${EVOLUTION_ICONS[m.evolution] ?? ""} ${m.evolution}`
                                : "—"}
                            </td>
                            <td className="py-2 text-xs text-gray-500">
                              {m.regions.length > 0 ? m.regions.join(", ") : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Alertes par département */}
              {alertesDeptData.length > 0 && (
                <div className="rounded-[12px] border bg-white p-5" style={{ borderColor: "#E5E7EB" }}>
                  <SectionTitle>Alertes par département</SectionTitle>
                  <VerticalBar data={alertesDeptData} color="#f97316" height={280} />
                </div>
              )}

              {/* Zones de risque */}
              {carto && carto.zonesRisque.length > 0 && (
                <div>
                  <SectionTitle>Zones de risque</SectionTitle>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {carto.zonesRisque.map((zone) => (
                      <div
                        key={zone.zone}
                        className="rounded-[12px] border bg-white p-5"
                        style={{
                          borderColor: zone.alertesActives > 0 ? "#fca5a5" : "#E5E7EB",
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-[#0B1F16]">{zone.zone}</p>
                          {zone.alertesActives > 0 && (
                            <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold text-white">
                              {zone.alertesActives} alerte{zone.alertesActives > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          {zone.departements.join(" · ")}
                        </p>
                        {zone.maladiesActives.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {zone.maladiesActives.map((m) => (
                              <span
                                key={m}
                                className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700"
                              >
                                {m}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bouton alerte */}
              <div>
                <button
                  onClick={() => setAlertOpen(true)}
                  className="h-10 rounded-[10px] border px-4 text-left cursor-pointer text-sm"
                  style={{ borderColor: "#D1E2DD", background: "#F3F4F4", color: "#FF6B57" }}
                >
                  Lancer un message d'alerte
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ── Modal alerte ── */}
      {alertOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4"
          onClick={() => setAlertOpen(false)}
        >
          <div
            className="w-[520px] max-w-[95vw] bg-white rounded-[12px] border shadow"
            style={{ borderColor: "#E5E7EB" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-gray-600">
              Les citoyens inscrits sur la plateforme ainsi que les médecins et membres du
              ministère recevront une alerte préventive concernant cette donnée.
            </div>
            <div className="px-6 pb-5 flex items-center justify-between">
              <button onClick={() => setAlertOpen(false)} className="h-10 px-4 rounded-[10px] text-[#00553E]">
                Annuler
              </button>
              <button
                onClick={() => {
                  setNotifs((prev) => {
                    const next = [
                      {
                        id: crypto.randomUUID(),
                        text: "Alerte préventive envoyée par le Ministère",
                        time: "à l'instant",
                        read: false,
                      },
                      ...prev,
                    ];
                    saveNotifs(next);
                    return next;
                  });
                  setAlertOpen(false);
                }}
                className="h-10 px-6 rounded-[10px] text-white"
                style={{ background: "#00553E" }}
              >
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
